import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Subject, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { KeyValuePair } from '@morningstar/shared/models';
import { StoreService } from './store-service';

export abstract class ApiService<AT = any> extends StoreService {

  /**
   * Description of data type to be used in snackbar messages
   */
  abstract dataDescription;

  private datum = new Subject<AT>();
  /**
   * Used for get method. Returns single object.
   */
  public datum$ = this.datum.asObservable();

  private data = new Subject<AT[]>();
  /**
   * Used for getList method. Returns array.
   */
  public data$ = this.data.asObservable();

  private keepEnding = ['ay'];

  constructor(protected httpClient: HttpClient, @Inject('API_BASE_URL') protected baseUrl: string,
              protected snackbar: MatSnackBar) {
    super(httpClient, baseUrl, snackbar);
  }

  get(endpoint: string, params?: KeyValuePair[]): Observable<AT> {

    if (params) {
      endpoint = this.replaceParams(endpoint, params);
    }

    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.get<AT>(url).pipe(
      tap(datum => this.datum.next(datum)),
      catchError(error => {
        this.setSnackbarMessage(`Error retrieving ${this.dataDescription}`);
        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  getList(endpoint: string, params?: KeyValuePair[]): Observable<AT[]> {

    if (params) {
      endpoint = this.replaceParams(endpoint, params);
    }

    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);
    this.setErrorMessage(null);

    return this.httpClient.get<AT[]>(url).pipe(
      tap(data => {
        this.setData(data);
      }),
      catchError(error => {
        this.setErrorMessage(error.error);
        this.setSnackbarMessage(`Error retrieving ${this.getPluralDescription()}`);
        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  add<AT, R = void>(datum: AT, endpoint: string, params?: KeyValuePair[]): Observable<R> {
    if (params) {
      endpoint = this.replaceParams(endpoint, params);
    }

    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.post<R>(url, datum).pipe(
      tap(_ => {
        this.setSnackbarMessage(`Saved ${this.dataDescription}`);
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          this.setSnackbarMessage(`Error saving ${this.dataDescription}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  update(datum: AT, endpoint: string, params?: KeyValuePair[]): Observable<void> {
    if (params) {
      endpoint = this.replaceParams(endpoint, params);
    }

    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.put<void>(url, datum).pipe(
      tap(_ => {
        this.setSnackbarMessage(`Saved ${this.dataDescription}`);
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          this.setSnackbarMessage(`Error saving ${this.dataDescription}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  updateList(datum: AT[], endpoint: string, params?: KeyValuePair[]): Observable<void> {
    if (params) {
      endpoint = this.replaceParams(endpoint, params);
    }

    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.put<void>(url, datum).pipe(
      tap(_ => {
        this.setSnackbarMessage(`Saved ${this.getPluralDescription()}`);
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          this.setSnackbarMessage(`Error saving ${this.getPluralDescription()}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  remove(endpoint: string, params?: KeyValuePair[], showSnackbar: boolean = true): Observable<void> {
    if (params) {
      endpoint = this.replaceParams(endpoint, params);
    }

    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.delete<void>(url).pipe(
      tap(_ => {
        if (showSnackbar) {
          this.setSnackbarMessage(`Removed ${this.dataDescription}`);
        }
        
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          this.setSnackbarMessage(`Error removing ${this.dataDescription}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  setData(data: AT[]) {
    this.data.next(data);
  }

  setDatum(datum: AT) {
    this.datum.next(datum);
  }

  getPluralDescription() {
    const description = this.dataDescription;
    
    if (description[description.length - 1] === 'y' && 
        !this.keepEnding.some(end => description.endsWith(end))) {
        return `${description.slice(0, -1)}ies`;
    }
    return `${description}s`;
  }
}
